// Configurações do sistema administrativo
export const ADMIN_CONFIG = {
  // ID do usuário admin (atualizado após diagnóstico)
  ADMIN_USER_ID: 'bd52acbe-1b3c-4606-a2f5-0c7bd143fe31',
  
  // Email do usuário admin
  ADMIN_EMAIL: 'admin@inrock.app',
  
  // Configurações da API
  API: {
    BASE_URL: import.meta.env.VITE_SUPABASE_URL || '',
    ENDPOINTS: {
      ADMIN_USERS: '/rest/v1/admin_users',
      ADMIN_LOGS: '/rest/v1/admin_logs',
      ADMIN_METRICS: '/rest/v1/admin_metrics',
      ADMIN_SETTINGS: '/rest/v1/admin_global_settings',
      ADMIN_NOTIFICATIONS: '/rest/v1/admin_notifications',
    }
  },
  
  // Configurações de autenticação
  AUTH: {
    // Tempo de expiração do token (em segundos)
    TOKEN_EXPIRY: 3600, // 1 hora
    
    // Tempo de refresh (em segundos)
    REFRESH_THRESHOLD: 300, // 5 minutos antes de expirar
  },
  
  // Configurações de logging
  LOGGING: {
    // Níveis de log disponíveis
    LEVELS: ['info', 'warning', 'error', 'critical'] as const,
    
    // Ações que devem ser logadas
    LOGGED_ACTIONS: [
      'user_login',
      'user_logout',
      'user_created',
      'user_updated',
      'user_deleted',
      'settings_changed',
      'data_exported',
      'backup_created',
      'system_maintenance',
    ] as const,
  },
  
  // Configurações de notificações
  NOTIFICATIONS: {
    // Tipos de notificação
    TYPES: ['info', 'success', 'warning', 'error'] as const,
    
    // Canais de notificação
    CHANNELS: ['email', 'in_app', 'webhook'] as const,
    
    // Configurações de email
    EMAIL: {
      FROM: 'admin@inrock.app',
      REPLY_TO: 'support@inrock.app',
      TEMPLATES: {
        WELCOME: 'welcome_admin',
        PASSWORD_RESET: 'password_reset',
        SECURITY_ALERT: 'security_alert',
        SYSTEM_UPDATE: 'system_update',
      }
    }
  },
  
  // Configurações de segurança
  SECURITY: {
    // Tempo máximo de inatividade (em minutos)
    SESSION_TIMEOUT: 30,
    
    // Número máximo de tentativas de login
    MAX_LOGIN_ATTEMPTS: 5,
    
    // Tempo de bloqueio após falhas (em minutos)
    LOCKOUT_DURATION: 15,
    
    // Requer 2FA para usuários admin
    REQUIRE_2FA: true,
    
    // IPs permitidos (whitelist)
    ALLOWED_IPS: [
      '127.0.0.1',      // Localhost
      '::1',            // Localhost IPv6
      // Adicione IPs específicos se necessário
    ],
  },
  
  // Configurações de backup
  BACKUP: {
    // Frequência de backup automático
    AUTO_BACKUP_INTERVAL: '0 2 * * *', // Diário às 2h da manhã
    
    // Retenção de backups (em dias)
    RETENTION_DAYS: 30,
    
    // Tamanho máximo de backup (em MB)
    MAX_SIZE_MB: 1000,
    
    // Compressão de backup
    COMPRESS: true,
  },
  
  // Configurações de monitoramento
  MONITORING: {
    // Intervalo de verificação de saúde (em segundos)
    HEALTH_CHECK_INTERVAL: 60,
    
    // Métricas a serem monitoradas
    METRICS: [
      'database_connections',
      'api_response_time',
      'error_rate',
      'active_users',
      'system_memory',
      'disk_usage',
    ],
    
    // Alertas automáticos
    ALERTS: {
      HIGH_ERROR_RATE: 0.05,      // 5% de taxa de erro
      HIGH_RESPONSE_TIME: 2000,   // 2 segundos
      LOW_DISK_SPACE: 0.1,        // 10% de espaço livre
      HIGH_MEMORY_USAGE: 0.9,     // 90% de uso de memória
    }
  }
};

// Tipos TypeScript para as configurações
export type LogLevel = typeof ADMIN_CONFIG.LOGGING.LEVELS[number];
export type LoggedAction = typeof ADMIN_CONFIG.LOGGING.LOGGED_ACTIONS[number];
export type NotificationType = typeof ADMIN_CONFIG.NOTIFICATIONS.TYPES[number];
export type NotificationChannel = typeof ADMIN_CONFIG.NOTIFICATIONS.CHANNELS[number];

// Função para obter URL completa da API
export const getApiUrl = (endpoint: string, params?: Record<string, string>) => {
  const baseUrl = ADMIN_CONFIG.API.BASE_URL;
  const fullEndpoint = ADMIN_CONFIG.API.ENDPOINTS[endpoint as keyof typeof ADMIN_CONFIG.API.ENDPOINTS] || endpoint;
  
  if (params) {
    const queryString = new URLSearchParams(params).toString();
    return `${baseUrl}${fullEndpoint}?${queryString}`;
  }
  
  return `${baseUrl}${fullEndpoint}`;
};

// Função para obter URL específica do usuário admin
export const getAdminUserUrl = (userId?: string) => {
  const id = userId || ADMIN_CONFIG.ADMIN_USER_ID;
  return getApiUrl('ADMIN_USERS', { select: '*', id: `eq.${id}` });
};

// Função para validar configurações
export const validateConfig = () => {
  const errors: string[] = [];
  
  if (!ADMIN_CONFIG.ADMIN_USER_ID) {
    errors.push('ADMIN_USER_ID não está configurado');
  }
  
  if (!ADMIN_CONFIG.ADMIN_EMAIL) {
    errors.push('ADMIN_EMAIL não está configurado');
  }
  
  if (!ADMIN_CONFIG.API.BASE_URL) {
    errors.push('API BASE_URL não está configurado');
  }
  
  if (errors.length > 0) {
    console.error('❌ Erros de configuração:', errors);
    return false;
  }
  
  console.log('✅ Configuração válida');
  return true;
};

// Exportar configuração validada
export default ADMIN_CONFIG;
